import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ReimbursementService } from 'src/app/core/services/reimbursement.service';
import { downloadFile } from 'src/app/helpers/utils';
import {
  ReimbursementModel,
  ReimbursementStatus,
  ReimbursementSubmissionModel,
  ReimbursementTypeModel
} from 'src/app/models/reimbursementModel';
import { AlertService } from 'src/app/shared/services/alert.service';

@Component({
  selector: 'app-reimbursements',
  templateUrl: './reimbursements.component.html',
  standalone: false
})
export class ReimbursementsComponent implements OnInit {
  @ViewChild('receiptInput') receiptInput: ElementRef<HTMLInputElement>;

  reimbursements: ReimbursementModel[] = [];
  reimbursementTypes: ReimbursementTypeModel[] = [];
  loading = true;
  submitting = false;
  editingID: number = null;
  selectedReceipt: File = null;
  maximumExpenseDate = new Date().toISOString().substring(0, 10);
  reimbursementStatus = ReimbursementStatus;

  request: ReimbursementSubmissionModel = this.emptyRequest();

  constructor(
    private reimbursementService: ReimbursementService,
    private alertService: AlertService) {
  }

  ngOnInit(): void {
    this.reimbursementService.getActiveTypes().subscribe({
      next: types => {
        this.reimbursementTypes = types;
        if (!this.request.reimbursementTypeID && types.length) {
          this.request.reimbursementTypeID = types[0].id;
        }
      },
      error: error => this.alertService.error(this.errorMessage(error, 'Unable to load reimbursement types.'))
    });
    this.loadReimbursements();
  }

  loadReimbursements(): void {
    this.loading = true;
    this.reimbursementService.getMyReimbursements().subscribe({
      next: reimbursements => {
        this.reimbursements = reimbursements;
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.alertService.error(this.errorMessage(error, 'Unable to load reimbursement requests.'));
      }
    });
  }

  receiptChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedReceipt = input.files?.length ? input.files[0] : null;
  }

  submit(): void {
    const validationError = this.validateRequest();
    if (validationError) {
      this.alertService.error(validationError);
      return;
    }

    this.submitting = true;
    const input = { ...this.request, receipt: this.selectedReceipt };
    const save = this.editingID
      ? this.reimbursementService.resubmit(this.editingID, input)
      : this.reimbursementService.submit(input);

    save.subscribe({
      next: saved => {
        const existingIndex = this.reimbursements.findIndex(item => item.id === saved.id);
        if (existingIndex >= 0) {
          this.reimbursements.splice(existingIndex, 1, saved);
        } else {
          this.reimbursements.unshift(saved);
        }
        this.reimbursements = [...this.reimbursements];
        this.alertService.success(this.editingID ? 'Reimbursement request resubmitted.' : 'Reimbursement request submitted.');
        this.cancelEdit();
        this.submitting = false;
      },
      error: error => {
        this.submitting = false;
        this.alertService.error(this.errorMessage(error, 'Unable to submit the reimbursement request.'));
      }
    });
  }

  edit(reimbursement: ReimbursementModel): void {
    if (reimbursement.statusID !== ReimbursementStatus.Denied) {
      return;
    }

    this.editingID = reimbursement.id;
    this.selectedReceipt = null;
    this.request = {
      reimbursementTypeID: reimbursement.reimbursementTypeID,
      expenseDate: reimbursement.expenseDate.substring(0, 10),
      merchant: reimbursement.merchant,
      description: reimbursement.description,
      requestedAmount: reimbursement.requestedAmount,
      confirmed: false
    };
    if (this.receiptInput) {
      this.receiptInput.nativeElement.value = '';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  cancelEdit(): void {
    this.editingID = null;
    this.selectedReceipt = null;
    this.request = this.emptyRequest();
    if (this.receiptInput) {
      this.receiptInput.nativeElement.value = '';
    }
  }

  downloadReceipt(reimbursement: ReimbursementModel): void {
    this.reimbursementService.downloadReceipt(reimbursement.receiptAttachmentID).subscribe({
      next: file => downloadFile(file, reimbursement.receiptFileName, true),
      error: error => this.alertService.error(this.errorMessage(error, 'Unable to download the receipt.'))
    });
  }

  statusClass(statusID: number): string {
    if (statusID === ReimbursementStatus.Approved) {
      return 'badge-success';
    }
    if (statusID === ReimbursementStatus.Denied) {
      return 'badge-danger';
    }
    return 'badge-warning';
  }

  private emptyRequest(): ReimbursementSubmissionModel {
    return {
      reimbursementTypeID: this.reimbursementTypes[0]?.id || null,
      expenseDate: '',
      merchant: '',
      description: '',
      requestedAmount: null,
      confirmed: false
    };
  }

  private validateRequest(): string {
    if (!this.request.expenseDate || !this.request.merchant?.trim() || !this.request.description?.trim()
        || !this.request.requestedAmount || this.request.requestedAmount <= 0) {
      return 'Type, expense date, merchant, description, and an amount greater than zero are required.';
    }
    if (!this.editingID && !this.selectedReceipt) {
      return 'A receipt or image is required.';
    }
    if (this.selectedReceipt && this.selectedReceipt.size > 10 * 1024 * 1024) {
      return 'Receipt must be no more than 10 MB.';
    }
    if (!this.request.confirmed) {
      return 'Confirm that the request is accurate before submitting.';
    }
    return null;
  }

  private errorMessage(error: any, fallback: string): string {
    return typeof error?.error === 'string' ? error.error : fallback;
  }
}
